// AdminAllAlumniDetailsDialog.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  Box,
  CircularProgress,
  Button,
  Paper,
  Typography,
  TextField,
  Autocomplete,
  Grid,
  Chip,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import UpdateIcon from "@mui/icons-material/Update";
import AddIcon from "@mui/icons-material/Add";
import DialogActions from "@mui/material/DialogActions";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { API_ENDPOINTS } from "config/api";
import { decryptText } from "config/encryption";
import safeToast from "utils/safeToast";
import axiosInstance from "API/axiosInstance";

// Get staffId and token from localStorage
const getAdminSession = () => {
  try {
    const session = localStorage.getItem("adminLogsessiondetails") || localStorage.getItem("adminsessiondetails");
    if (session) {
      const decrypted = decryptText(session);
      if (decrypted) {
        const parsed = JSON.parse(decrypted);
        return {
          staffId: parsed.StaffId || parsed.staffId || parsed.StaffCode || parsed.staffCode || null,
          token: parsed.Token || parsed.token || null
        };
      }
    }
  } catch (error) {
    console.error("Error extracting admin session:", error);
  }
  return { staffId: null, token: null };
};

const { staffId, token } = getAdminSession();

const AdminAllAlumniDetailsDialog = ({ open, onClose }) => {
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editedCells, setEditedCells] = useState(new Map()); // Map<field, Set<studentId>>
  const [originalData, setOriginalData] = useState(new Map()); // Map<studentId, originalRowData>
  const [editingCell, setEditingCell] = useState(null); // {rowIndex, field}
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'info' }); // MUI Alert state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addFormData, setAddFormData] = useState({
    fullName: '',
    rollnum: '',
    customGender: '',
    customDob: '',
    customBatch: '',
    customCollegeStudied: '',
    customCourseStudied: '',
    customBranch: '',
    customWhatsappNumber: '',
    customPresentStatus: '',
    customOrganization: '',
    customDesignation: '',
    customLocation: '',
    customAvailability: '',
    domain: '',
    customCategory: ''
  });
  const [addFormErrors, setAddFormErrors] = useState({});
  const [addingAlumni, setAddingAlumni] = useState(false);
  
  // Refs to store latest state
  const editedCellsRef = useRef(editedCells);
  const editModeRef = useRef(editMode);
  const rowDataRef = useRef(rowData);
  
  // Update refs when state changes
  useEffect(() => {
    editedCellsRef.current = editedCells;
  }, [editedCells]);
  
  useEffect(() => {
    editModeRef.current = editMode;
  }, [editMode]);

  useEffect(() => {
    rowDataRef.current = rowData;
  }, [rowData]);
  
  // Filter states - using arrays for multi-select
  const [registrationType, setRegistrationType] = useState(["0"]);
  const [selectedBatch, setSelectedBatch] = useState([]);
  const [selectedGender, setSelectedGender] = useState([]);
  const [selectedCollege, setSelectedCollege] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState([]);
  const [selectedDesignation, setSelectedDesignation] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState([]);
  const [selectedAvailability, setSelectedAvailability] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState([]);

  // Filter options
  const registrationOptions = [
    { value: "0", label: "All Alumni" },
    { value: "1", label: "Registered Users" },
    { value: "2", label: "Unregistered Alumni" }
  ];

  // Dropdown options loaded from database
  const [dropdownOptions, setDropdownOptions] = useState({
    batches: [],
    genders: [],
    colleges: [],
    courses: [],
    branches: [],
    statuses: [],
    organizations: [],
    designations: [],
    locations: [],
    availabilities: [],
    domains: [],
    categories: []
  });

  const [loadingDropdowns, setLoadingDropdowns] = useState(false);

  // Fetch dropdown options from database
  const fetchDropdownOptions = useCallback(async () => {
    try {
      setLoadingDropdowns(true);
      const response = await axiosInstance.get(API_ENDPOINTS.ADMIN.GET_FILTER_DROPDOWN_OPTIONS);
      const data = response.data || {};
      
      setDropdownOptions({
        batches: (data.batches || []).map(item => ({ value: item.value, label: item.label })),
        genders: (data.genders || []).map(item => ({ value: item.value, label: item.label })),
        colleges: (data.colleges || []).map(item => ({ value: item.value, label: item.label })),
        courses: (data.courses || []).map(item => ({ value: item.value, label: item.label })),
        branches: (data.branches || []).map(item => ({ value: item.value, label: item.label })),
        statuses: (data.statuses || []).map(item => ({ value: item.value, label: item.label })),
        organizations: (data.organizations || []).map(item => ({ value: item.value, label: item.label })),
        designations: (data.designations || []).map(item => ({ value: item.value, label: item.label })),
        locations: (data.locations || []).map(item => ({ value: item.value, label: item.label })),
        availabilities: (data.availabilities || []).map(item => ({ value: item.value, label: item.label })),
        domains: (data.domains || []).map(item => ({ value: item.value, label: item.label })),
        categories: (data.categories || []).map(item => ({ value: item.value, label: item.label }))
      });
    } catch (error) {
      console.error("Error fetching dropdown options:", error);
      setAlert({
        open: true,
        message: "Failed to load filter options. Please try again.",
        severity: 'error'
      });
    } finally {
      setLoadingDropdowns(false);
    }
  }, []);

  // Handle cell edit - for MUI DataTable with duplicate validation
  const handleCellEdit = useCallback((rowIndex, field, newValue) => {
    const row = rowData[rowIndex];
    if (!row || !row.studentId) return;

    const oldValue = row[field];
    if (oldValue === newValue) {
      return;
    }

    // Check for duplicates for RollNum and WhatsApp
    if (field === 'rollnum' || field === 'customWhatsappNumber') {
      const trimmedValue = (newValue || '').trim();
      if (trimmedValue) {
        // Check if this value already exists in other rows (excluding current row)
        const isDuplicate = rowData.some((r, idx) => 
          idx !== rowIndex && 
          r && 
          r[field] && 
          r[field].trim().toLowerCase() === trimmedValue.toLowerCase()
        );

        if (isDuplicate) {
          setAlert({
            open: true,
            message: `${field === 'rollnum' ? 'Roll Number' : 'WhatsApp Number'} already exists. Please use a unique value.`,
            severity: 'error'
          });
          return; // Don't save if duplicate
        }
      }
    }

    // Update row data
    setRowData(prev => {
      const newData = [...prev];
      newData[rowIndex] = { ...newData[rowIndex], [field]: newValue };
      return newData;
    });

    // Track edit
    setEditedCells(prev => {
      const newMap = new Map(prev);
      if (!newMap.has(field)) {
        newMap.set(field, new Set());
      }
      newMap.get(field).add(row.studentId);
      return newMap;
    });
  }, [rowData]);


  // Update all edited records across all columns
  const handleUpdateAll = useCallback(async () => {
    const currentEditedCells = editedCellsRef.current;
    
    if (!currentEditedCells || currentEditedCells.size === 0) {
      setAlert({
        open: true,
        message: 'No edits found to update',
        severity: 'warning'
      });
      return;
    }

    const totalEdits = Array.from(currentEditedCells.values()).reduce((sum, set) => sum + set.size, 0);
    if (totalEdits === 0) {
      setAlert({
        open: true,
        message: 'No edits found to update',
        severity: 'warning'
      });
      return;
    }

    // Validate for duplicates before saving
    const currentRowData = rowDataRef.current;
    const duplicateErrors = [];
    
    // Check for duplicate RollNum
    const rollnumValues = new Map();
    currentRowData.forEach((row, idx) => {
      if (row && row.rollnum && row.rollnum.trim()) {
        const key = row.rollnum.trim().toLowerCase();
        if (rollnumValues.has(key)) {
          duplicateErrors.push(`Duplicate Roll Number "${row.rollnum}" found at row ${rollnumValues.get(key) + 1} and ${idx + 1}`);
        } else {
          rollnumValues.set(key, idx);
        }
      }
    });

    // Check for duplicate WhatsApp
    const whatsappValues = new Map();
    currentRowData.forEach((row, idx) => {
      if (row && row.customWhatsappNumber && row.customWhatsappNumber.trim()) {
        const key = row.customWhatsappNumber.trim().toLowerCase();
        if (whatsappValues.has(key)) {
          duplicateErrors.push(`Duplicate WhatsApp Number "${row.customWhatsappNumber}" found at row ${whatsappValues.get(key) + 1} and ${idx + 1}`);
        } else {
          whatsappValues.set(key, idx);
        }
      }
    });

    if (duplicateErrors.length > 0) {
      setAlert({
        open: true,
        message: `Cannot save: ${duplicateErrors[0]}`,
        severity: 'error'
      });
      console.error('Duplicate validation errors:', duplicateErrors);
      return;
    }

    console.log(`Updating all ${totalEdits} edited cells across ${currentEditedCells.size} columns`);

    try {
      setSaving(true);
      let totalSuccess = 0;
      let totalErrors = 0;

      // Process each column that has edits
      for (const [field, editedStudentIds] of currentEditedCells.entries()) {
        if (!editedStudentIds || editedStudentIds.size === 0) continue;

        // Get all rows that have edits in this column - use ref to get latest data
        const currentRowData = rowDataRef.current;
        const rowsToUpdate = currentRowData.filter(row => 
          row && row.studentId && editedStudentIds.has(row.studentId)
        );

        console.log(`Found ${rowsToUpdate.length} rows to update for field ${field}`);
        if (rowsToUpdate.length === 0) {
          console.warn(`No rows found to update for field ${field} with studentIds:`, Array.from(editedStudentIds));
          continue;
        }

        // Update each row in this column
        for (const row of rowsToUpdate) {
          try {
            // Build update payload - only include the changed field
            const payload = {
              studentId: row.studentId,
              updatedBy: staffId
            };

            // Map field names to API field names
            const fieldMapping = {
              'fullName': 'fullName',
              'rollnum': 'rollNum',
              'customGender': 'customGender',
              'customDob': 'customDob',
              'customBatch': 'customBatch',
              'customCollegeStudied': 'customCollegeStudied',
              'customCourseStudied': 'customCourseStudied',
              'customBranch': 'customBranch',
              'customWhatsappNumber': 'customWhatsappNumber',
              'customPresentStatus': 'customPresentStatus',
              'customOrganization': 'customOrganization',
              'customDesignation': 'customDesignation',
              'customLocation': 'customLocation',
              'customAvailability': 'customAvailability',
              'domain': 'domain',
              'customCategory': 'customCategory'
            };

            const apiField = fieldMapping[field];
            if (apiField) {
              // Handle both camelCase and PascalCase from API response
              // For customDob, check both camelCase and PascalCase
              let fieldValue = row[field];
              if (fieldValue === undefined || fieldValue === null) {
                // Try PascalCase version
                const pascalCase = field.charAt(0).toUpperCase() + field.slice(1);
                fieldValue = row[pascalCase];
              }
              payload[apiField] = fieldValue !== undefined && fieldValue !== null ? fieldValue : null;
            } else {
              console.warn(`No API field mapping found for grid field: ${field}`);
              continue; // Skip this row if field mapping is missing
            }

            console.log(`Updating student ${row.studentId} field ${field}:`, payload);
            console.log(`API Endpoint:`, API_ENDPOINTS.ADMIN.UPDATE_ALUMNI_DETAILS);
            const response = await axiosInstance.post(API_ENDPOINTS.ADMIN.UPDATE_ALUMNI_DETAILS, payload);
            console.log(`Update response for student ${row.studentId}:`, response.data);
            if (response.data && response.data.message) {
              console.log(`Success message:`, response.data.message);
            }
            totalSuccess++;
          } catch (error) {
            console.error(`Error updating student ${row.studentId} for field ${field}:`, error);
            console.error('Error details:', error.response?.data || error.message);
            totalErrors++;
          }
        }
      }

      if (totalSuccess > 0) {
        setAlert({
          open: true,
          message: `Successfully updated ${totalSuccess} record(s)`,
          severity: 'success'
        });
        
        // Clear all edited cells
        setEditedCells(new Map());

        // Refresh data by re-fetching
        fetchAlumniData();
      } else if (totalErrors > 0) {
        setAlert({
          open: true,
          message: `${totalErrors} record(s) failed to update. Check console for details.`,
          severity: 'error'
        });
      } else {
        setAlert({
          open: true,
          message: 'No records were updated. Please check the console for details.',
          severity: 'warning'
        });
      }
    } catch (error) {
      console.error("Error updating all records:", error);
      setAlert({
        open: true,
        message: "Failed to update changes. Please try again.",
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  }, [staffId, rowData]);


  // Create editable cell component - all fields are text fields now
  const EditableCell = ({ rowIndex, field, value, onSave, error }) => {
    const [editValue, setEditValue] = useState(value || '');
    const [isEditing, setIsEditing] = useState(false);
    const isEdited = editedCells.has(field) && editedCells.get(field)?.has(rowData[rowIndex]?.studentId);

    const handleSave = () => {
      if (editValue !== value) {
        onSave(rowIndex, field, editValue);
      }
      setIsEditing(false);
    };

    const handleCancel = () => {
      setEditValue(value || '');
      setIsEditing(false);
    };

    if (!editMode || !isEditing) {
      return (
        <Box
          onClick={() => editMode && setIsEditing(true)}
          sx={{
            cursor: editMode ? 'pointer' : 'default',
            backgroundColor: isEdited ? '#fff3cd' : (error ? '#ffebee' : 'transparent'),
            p: 0.5,
            borderRadius: 1,
            '&:hover': editMode ? { backgroundColor: '#f0f0f0' } : {}
          }}
        >
          <MDTypography variant="caption" color={error ? 'error' : 'text'}>
            {value || '-'}
          </MDTypography>
        </Box>
      );
    }

    return (
      <TextField
        size="small"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSave();
          if (e.key === 'Escape') handleCancel();
        }}
        error={error}
        helperText={error ? 'Duplicate value found' : ''}
        autoFocus
        sx={{ minWidth: 120 }}
      />
    );
  };

  EditableCell.propTypes = {
    rowIndex: PropTypes.number,
    field: PropTypes.string,
    value: PropTypes.any,
    onSave: PropTypes.func,
    error: PropTypes.bool,
  };

  // Table data for MUI DataTable
  const tableData = useMemo(() => {
    const columns = [
      { Header: "S.No", accessor: "sNo", width: "5%", align: "center" },
      { Header: "Full Name", accessor: "fullName", width: "12%", align: "left" },
      { Header: "Roll Number", accessor: "rollnum", width: "8%", align: "left" },
      { Header: "Gender", accessor: "customGender", width: "6%", align: "left" },
      { Header: "Date of Birth", accessor: "customDob", width: "8%", align: "left" },
      { Header: "Batch", accessor: "customBatch", width: "6%", align: "left" },
      { Header: "College", accessor: "customCollegeStudied", width: "10%", align: "left" },
      { Header: "Course", accessor: "customCourseStudied", width: "8%", align: "left" },
      { Header: "Branch", accessor: "customBranch", width: "7%", align: "left" },
      { Header: "WhatsApp", accessor: "customWhatsappNumber", width: "8%", align: "left" },
      { Header: "Status", accessor: "customPresentStatus", width: "8%", align: "left" },
      { Header: "Organization", accessor: "customOrganization", width: "10%", align: "left" },
      { Header: "Designation", accessor: "customDesignation", width: "9%", align: "left" },
      { Header: "Location", accessor: "customLocation", width: "7%", align: "left" },
      { Header: "Availability", accessor: "customAvailability", width: "6%", align: "left" },
      { Header: "Domain", accessor: "domain", width: "7%", align: "left" },
      { Header: "Category", accessor: "customCategory", width: "7%", align: "left" },
    ];

    // Helper function to check for duplicate values
    const checkDuplicate = (field, value, currentIndex) => {
      if (!value || typeof value !== 'string') return false;
      // eslint-disable-next-line react/prop-types
      const trimmedValue = value.trim();
      if (!trimmedValue) return false;
      const lowerValue = trimmedValue.toLowerCase();
      return rowData.some((r, idx) => {
        if (idx === currentIndex || !r || !r[field] || typeof r[field] !== 'string') {
          return false;
        }
        const otherTrimmed = r[field].trim();
        return otherTrimmed && otherTrimmed.toLowerCase() === lowerValue;
      });
    };

    const rows = rowData.map((row, index) => {
      return {
        sNo: (
          <MDTypography variant="caption" fontWeight="medium" textAlign="center">
            {index + 1}
          </MDTypography>
        ),
        fullName: (
          <EditableCell
            rowIndex={index}
            field="fullName"
            value={row.fullName}
            onSave={handleCellEdit}
          />
        ),
        rollnum: (
          <EditableCell
            rowIndex={index}
            field="rollnum"
            value={row.rollnum}
            onSave={handleCellEdit}
            error={checkDuplicate('rollnum', row.rollnum, index)}
          />
        ),
        customGender: (
          <EditableCell
            rowIndex={index}
            field="customGender"
            value={row.customGender}
            onSave={handleCellEdit}
          />
        ),
        customDob: (
          <EditableCell
            rowIndex={index}
            field="customDob"
            value={row.customDob || row.CustomDob || ""}
            onSave={handleCellEdit}
          />
        ),
        customBatch: (
          <EditableCell
            rowIndex={index}
            field="customBatch"
            value={row.customBatch}
            onSave={handleCellEdit}
          />
        ),
        customCollegeStudied: (
          <EditableCell
            rowIndex={index}
            field="customCollegeStudied"
            value={row.customCollegeStudied}
            onSave={handleCellEdit}
          />
        ),
        customCourseStudied: (
          <EditableCell
            rowIndex={index}
            field="customCourseStudied"
            value={row.customCourseStudied}
            onSave={handleCellEdit}
          />
        ),
        customBranch: (
          <EditableCell
            rowIndex={index}
            field="customBranch"
            value={row.customBranch}
            onSave={handleCellEdit}
          />
        ),
        customWhatsappNumber: (
          <EditableCell
            rowIndex={index}
            field="customWhatsappNumber"
            value={row.customWhatsappNumber}
            onSave={handleCellEdit}
            error={checkDuplicate('customWhatsappNumber', row.customWhatsappNumber, index)}
          />
        ),
        customPresentStatus: (
          <EditableCell
            rowIndex={index}
            field="customPresentStatus"
            value={row.customPresentStatus}
            onSave={handleCellEdit}
          />
        ),
        customOrganization: (
          <EditableCell
            rowIndex={index}
            field="customOrganization"
            value={row.customOrganization}
            onSave={handleCellEdit}
          />
        ),
        customDesignation: (
          <EditableCell
            rowIndex={index}
            field="customDesignation"
            value={row.customDesignation}
            onSave={handleCellEdit}
          />
        ),
        customLocation: (
          <EditableCell
            rowIndex={index}
            field="customLocation"
            value={row.customLocation}
            onSave={handleCellEdit}
          />
        ),
        customAvailability: (
          <EditableCell
            rowIndex={index}
            field="customAvailability"
            value={row.customAvailability}
            onSave={handleCellEdit}
          />
        ),
        domain: (
          <EditableCell
            rowIndex={index}
            field="domain"
            value={row.domain}
            onSave={handleCellEdit}
          />
        ),
        customCategory: (
          <EditableCell
            rowIndex={index}
            field="customCategory"
            value={row.customCategory}
            onSave={handleCellEdit}
          />
        ),
      };
    });

    return { columns, rows };
  }, [rowData, editMode, editedCells, handleCellEdit]);


  // Fetch alumni data with filters
  const fetchAlumniData = useCallback(async () => {
    try {
      setLoading(true);
      const payload = {
        registedType: Array.isArray(registrationType) ? registrationType.join(',') : registrationType,
        batch: Array.isArray(selectedBatch) ? selectedBatch.join(',') : selectedBatch,
        gender: Array.isArray(selectedGender) ? selectedGender.join(',') : selectedGender,
        college: Array.isArray(selectedCollege) ? selectedCollege.join(',') : selectedCollege,
        course: Array.isArray(selectedCourse) ? selectedCourse.join(',') : selectedCourse,
        branch: Array.isArray(selectedBranch) ? selectedBranch.join(',') : selectedBranch,
        status: Array.isArray(selectedStatus) ? selectedStatus.join(',') : selectedStatus,
        organization: Array.isArray(selectedOrganization) ? selectedOrganization.join(',') : selectedOrganization,
        designation: Array.isArray(selectedDesignation) ? selectedDesignation.join(',') : selectedDesignation,
        location: Array.isArray(selectedLocation) ? selectedLocation.join(',') : selectedLocation,
        availability: Array.isArray(selectedAvailability) ? selectedAvailability.join(',') : selectedAvailability,
        domain: Array.isArray(selectedDomain) ? selectedDomain.join(',') : (selectedDomain || ''),
        category: Array.isArray(selectedCategory) ? selectedCategory.join(',') : (selectedCategory || '')
      };
      
      console.log('Fetching alumni data with filters:', payload);
      console.log('Domain filter:', selectedDomain);
      console.log('Category filter:', selectedCategory);
      
      const response = await axiosInstance.post(API_ENDPOINTS.ADMIN.GET_ALUMNI_REPORTS_WITH_FILTERS, payload);
      const data = response.data || [];
      setRowData(data);
      // Store original data for comparison
      const originalMap = new Map();
      data.forEach(row => {
        if (row.studentId) {
          originalMap.set(row.studentId, { ...row });
        }
      });
      setOriginalData(originalMap);
      setEditedCells(new Map()); // Clear edited cells on new data load
    } catch (error) {
      console.error("Error fetching alumni data:", error);
      setAlert({
        open: true,
        message: "Failed to fetch alumni data. Please try again.",
        severity: 'error'
      });
      setRowData([]);
    } finally {
      setLoading(false);
    }
  }, [
    registrationType,
    selectedBatch,
    selectedGender,
    selectedCollege,
    selectedCourse,
    selectedBranch,
    selectedStatus,
    selectedOrganization,
    selectedDesignation,
    selectedLocation,
    selectedAvailability,
    selectedDomain,
    selectedCategory
  ]);

  // Handle Add Alumni
  const handleAddAlumni = useCallback(async () => {
    // Validate required fields
    const errors = {};
    if (!addFormData.fullName || !addFormData.fullName.trim()) {
      errors.fullName = 'Full Name is required';
    }
    if (!addFormData.rollnum || !addFormData.rollnum.trim()) {
      errors.rollnum = 'Roll Number is required';
    }
    if (!addFormData.customWhatsappNumber || !addFormData.customWhatsappNumber.trim()) {
      errors.customWhatsappNumber = 'WhatsApp Number is required';
    }

    if (Object.keys(errors).length > 0) {
      setAddFormErrors(errors);
      setAlert({
        open: true,
        message: 'Please fill in all required fields',
        severity: 'error'
      });
      return;
    }

    setAddingAlumni(true);
    setAddFormErrors({});

    try {
      const response = await axiosInstance.post(API_ENDPOINTS.ADMIN.INSERT_ALUMNI_DETAILS, {
        fullName: addFormData.fullName.trim(),
        rollNum: addFormData.rollnum.trim(),
        customGender: addFormData.customGender.trim() || null,
        customDob: addFormData.customDob.trim() || null,
        customBatch: addFormData.customBatch.trim() || null,
        customCollegeStudied: addFormData.customCollegeStudied.trim() || null,
        customCourseStudied: addFormData.customCourseStudied.trim() || null,
        customBranch: addFormData.customBranch.trim() || null,
        customWhatsappNumber: addFormData.customWhatsappNumber.trim(),
        customPresentStatus: addFormData.customPresentStatus.trim() || null,
        customOrganization: addFormData.customOrganization.trim() || null,
        customDesignation: addFormData.customDesignation.trim() || null,
        customLocation: addFormData.customLocation.trim() || null,
        customAvailability: addFormData.customAvailability.trim() || null,
        domain: addFormData.domain.trim() || null,
        customCategory: addFormData.customCategory.trim() || null,
        createdBy: staffId
      });

      if (response.data && response.data.message) {
        if (response.data.message.includes('Error') || response.data.message.includes('duplicate') || response.data.message.includes('already exists')) {
          setAlert({
            open: true,
            message: response.data.message,
            severity: 'error'
          });
        } else {
          setAlert({
            open: true,
            message: response.data.message || 'Alumni added successfully',
            severity: 'success'
          });
          // Close dialog and reset form
          setAddDialogOpen(false);
          setAddFormData({
            fullName: '',
            rollnum: '',
            customGender: '',
            customDob: '',
            customBatch: '',
            customCollegeStudied: '',
            customCourseStudied: '',
            customBranch: '',
            customWhatsappNumber: '',
            customPresentStatus: '',
            customOrganization: '',
            customDesignation: '',
            customLocation: '',
            customAvailability: '',
            domain: '',
            customCategory: ''
          });
          // Refresh data
          fetchAlumniData();
        }
      }
    } catch (error) {
      console.error('Error adding alumni:', error);
      // eslint-disable-next-line react/prop-types
      const errorMessage = error.response?.data?.message || error.response?.data?.details || 'Failed to add alumni. Please try again.';
      setAlert({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setAddingAlumni(false);
    }
  }, [addFormData, staffId, fetchAlumniData]);

  // Reset Add Alumni Form
  const handleResetAddForm = useCallback(() => {
    setAddFormData({
      fullName: '',
      rollnum: '',
      customGender: '',
      customBatch: '',
      customCollegeStudied: '',
      customCourseStudied: '',
      customBranch: '',
      customWhatsappNumber: '',
      customPresentStatus: '',
      customOrganization: '',
      customDesignation: '',
      customLocation: '',
      customAvailability: '',
      domain: '',
      customCategory: ''
    });
    setAddFormErrors({});
  }, []);

  // Export CSV function
  const exportCsv = useCallback(() => {
    if (!rowData || rowData.length === 0) {
      setAlert({
        open: true,
        message: 'No data to export',
        severity: 'warning'
      });
      return;
    }

    try {
      const headers = ['S.No', 'Full Name', 'Roll Number', 'Gender', 'Batch', 'College', 'Course', 'Branch', 'WhatsApp', 'Status', 'Organization', 'Designation', 'Location', 'Availability', 'Domain', 'Category'];
      const csvContent = [
        headers.join(','),
        ...rowData.map((row, index) => [
          index + 1,
          `"${(row.fullName || '').replace(/"/g, '""')}"`,
          `"${(row.rollnum || '').replace(/"/g, '""')}"`,
          `"${(row.customGender || '').replace(/"/g, '""')}"`,
          `"${(row.customBatch || '').replace(/"/g, '""')}"`,
          `"${(row.customCollegeStudied || '').replace(/"/g, '""')}"`,
          `"${(row.customCourseStudied || '').replace(/"/g, '""')}"`,
          `"${(row.customBranch || '').replace(/"/g, '""')}"`,
          `"${(row.customWhatsappNumber || '').replace(/"/g, '""')}"`,
          `"${(row.customPresentStatus || '').replace(/"/g, '""')}"`,
          `"${(row.customOrganization || '').replace(/"/g, '""')}"`,
          `"${(row.customDesignation || '').replace(/"/g, '""')}"`,
          `"${(row.customLocation || '').replace(/"/g, '""')}"`,
          `"${(row.customAvailability || '').replace(/"/g, '""')}"`,
          `"${(row.domain || '').replace(/"/g, '""')}"`,
          `"${(row.customCategory || '').replace(/"/g, '""')}"`,
        ].join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      const timestamp = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `all-alumni-details-${timestamp}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('CSV export error:', error);
      setAlert({
        open: true,
        message: 'Failed to export CSV. Please try again.',
        severity: 'error'
      });
    }
  }, [rowData]);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setRegistrationType(["0"]);
    setSelectedBatch([]);
    setSelectedGender([]);
    setSelectedCollege([]);
    setSelectedCourse([]);
    setSelectedBranch([]);
    setSelectedStatus([]);
    setSelectedOrganization([]);
    setSelectedDesignation([]);
    setSelectedLocation([]);
    setSelectedAvailability([]);
    setSelectedDomain([]);
    setSelectedCategory([]);
  }, []);

  // Count active filters
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    const regType = Array.isArray(registrationType) ? registrationType : [registrationType];
    if (regType.length > 0 && !regType.includes("0")) count++;
    if (Array.isArray(selectedBatch) && selectedBatch.length > 0) count++;
    if (Array.isArray(selectedGender) && selectedGender.length > 0) count++;
    if (Array.isArray(selectedCollege) && selectedCollege.length > 0) count++;
    if (Array.isArray(selectedCourse) && selectedCourse.length > 0) count++;
    if (Array.isArray(selectedBranch) && selectedBranch.length > 0) count++;
    if (Array.isArray(selectedStatus) && selectedStatus.length > 0) count++;
    if (Array.isArray(selectedOrganization) && selectedOrganization.length > 0) count++;
    if (Array.isArray(selectedDesignation) && selectedDesignation.length > 0) count++;
    if (Array.isArray(selectedLocation) && selectedLocation.length > 0) count++;
    if (Array.isArray(selectedAvailability) && selectedAvailability.length > 0) count++;
    if (Array.isArray(selectedDomain) && selectedDomain.length > 0) count++;
    if (Array.isArray(selectedCategory) && selectedCategory.length > 0) count++;
    return count;
  }, [
    registrationType,
    selectedBatch,
    selectedGender,
    selectedCollege,
    selectedCourse,
    selectedBranch,
    selectedStatus,
    selectedOrganization,
    selectedDesignation,
    selectedLocation,
    selectedAvailability,
    selectedDomain,
    selectedCategory
  ]);

  // Load dropdown options and data when dialog opens
  useEffect(() => {
    if (open) {
      fetchDropdownOptions();
      fetchAlumniData();
    }
  }, [open, fetchDropdownOptions]);



  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      {/* Title + Close Button */}
      <DialogTitle sx={{ pr: 8, fontWeight: "bold", color: "primary.main" }}>
        All Alumni Details - Manage & Edit
        <Box sx={{ position: "absolute", top: 8, right: 8 }}>
          <Tooltip title="Close">
            <IconButton onClick={onClose} color="error">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      {/* Content - Split Layout */}
      <DialogContent sx={{ p: 0, display: 'flex', height: 'calc(100vh - 64px)' }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: '100%', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Left Side - Filter Panel */}
            <Paper 
              elevation={2}
              sx={{ 
                width: 380,
                minWidth: 350,
                maxWidth: '30%',
                borderRight: 1,
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
            >
              {/* Filter Header */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'success.main', color: 'white' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FilterListIcon />
                    <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                      Filter Options
                    </Typography>
                  </Box>
                  {activeFiltersCount > 0 && (
                    <Chip 
                      label={activeFiltersCount} 
                      size="small" 
                      sx={{ 
                        bgcolor: 'white',
                        color: 'success.main',
                        fontWeight: 600
                      }}
                    />
                  )}
                </Box>
              </Box>

              {/* Active Filters Summary */}
              {activeFiltersCount > 0 && (
                <Box sx={{ p: 1.5, bgcolor: 'primary.lighter', borderBottom: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.dark', fontSize: '0.75rem' }}>
                      {activeFiltersCount} filter{activeFiltersCount > 1 ? 's' : ''} active
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<ClearIcon />}
                      onClick={clearAllFilters}
                      sx={{ fontSize: '0.7rem', minWidth: 'auto', py: 0.5 }}
                    >
                      Clear All
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Filter Content - Scrollable */}
              <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary', fontWeight: 600, fontSize: '0.8rem' }}>
                  Select filters to refine your search
                </Typography>
                <Grid container spacing={2}>
                  {/* Registration Type */}
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      size="small"
                      fullWidth
                      options={registrationOptions}
                      value={registrationOptions.filter(opt => 
                        Array.isArray(registrationType) 
                          ? registrationType.includes(opt.value)
                          : registrationType === opt.value
                      )}
                      onChange={(event, newValue) => {
                        const values = newValue.map(opt => opt.value);
                        setRegistrationType(values.length > 0 ? values : ["0"]);
                      }}
                      getOptionLabel={(option) => option.label || option}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Registration Type"
                          sx={{ 
                            '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                            '& .MuiInputBase-input': { fontSize: '0.85rem' }
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Batch */}
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      size="small"
                      fullWidth
                      loading={loadingDropdowns}
                      options={dropdownOptions.batches}
                      value={dropdownOptions.batches.filter(opt => 
                        Array.isArray(selectedBatch) 
                          ? selectedBatch.includes(opt.value)
                          : selectedBatch === opt.value
                      )}
                      onChange={(event, newValue) => {
                        const values = newValue.map(opt => opt.value);
                        setSelectedBatch(values);
                      }}
                      getOptionLabel={(option) => option.label || option.value || option}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Batch"
                          sx={{ 
                            '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                            '& .MuiInputBase-input': { fontSize: '0.85rem' }
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Gender */}
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      size="small"
                      fullWidth
                      loading={loadingDropdowns}
                      options={dropdownOptions.genders}
                      value={dropdownOptions.genders.filter(opt => 
                        Array.isArray(selectedGender) 
                          ? selectedGender.includes(opt.value)
                          : selectedGender === opt.value
                      )}
                      onChange={(event, newValue) => {
                        const values = newValue.map(opt => opt.value);
                        setSelectedGender(values);
                      }}
                      getOptionLabel={(option) => option.label || option.value || option}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Gender"
                          sx={{ 
                            '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                            '& .MuiInputBase-input': { fontSize: '0.85rem' }
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* College */}
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      size="small"
                      fullWidth
                      freeSolo
                      loading={loadingDropdowns}
                      options={dropdownOptions.colleges}
                      value={Array.isArray(selectedCollege) 
                        ? selectedCollege.map(col => typeof col === 'string' ? { value: col, label: col } : col)
                        : selectedCollege ? [{ value: selectedCollege, label: selectedCollege }] : []
                      }
                      onChange={(event, newValue) => {
                        const values = newValue.map(item => typeof item === 'string' ? item : item.value);
                        setSelectedCollege(values);
                      }}
                      getOptionLabel={(option) => typeof option === 'string' ? option : (option.label || option.value || option)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="College"
                          sx={{ 
                            '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                            '& .MuiInputBase-input': { fontSize: '0.85rem' }
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Course */}
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      size="small"
                      fullWidth
                      loading={loadingDropdowns}
                      options={dropdownOptions.courses}
                      value={dropdownOptions.courses.filter(opt => 
                        Array.isArray(selectedCourse) 
                          ? selectedCourse.includes(opt.value)
                          : selectedCourse === opt.value
                      )}
                      onChange={(event, newValue) => {
                        const values = newValue.map(opt => opt.value);
                        setSelectedCourse(values);
                      }}
                      getOptionLabel={(option) => option.label || option}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Course"
                          sx={{ 
                            '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                            '& .MuiInputBase-input': { fontSize: '0.85rem' }
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Branch */}
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      size="small"
                      fullWidth
                      loading={loadingDropdowns}
                      options={dropdownOptions.branches}
                      value={dropdownOptions.branches.filter(opt => 
                        Array.isArray(selectedBranch) 
                          ? selectedBranch.includes(opt.value)
                          : selectedBranch === opt.value
                      )}
                      onChange={(event, newValue) => {
                        const values = newValue.map(opt => opt.value);
                        setSelectedBranch(values);
                      }}
                      getOptionLabel={(option) => option.label || option}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Branch"
                          sx={{ 
                            '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                            '& .MuiInputBase-input': { fontSize: '0.85rem' }
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Status */}
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      size="small"
                      fullWidth
                      loading={loadingDropdowns}
                      options={dropdownOptions.statuses}
                      value={dropdownOptions.statuses.filter(opt => 
                        Array.isArray(selectedStatus) 
                          ? selectedStatus.includes(opt.value)
                          : selectedStatus === opt.value
                      )}
                      onChange={(event, newValue) => {
                        const values = newValue.map(opt => opt.value);
                        setSelectedStatus(values);
                      }}
                      getOptionLabel={(option) => option.label || option}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Status"
                          sx={{ 
                            '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                            '& .MuiInputBase-input': { fontSize: '0.85rem' }
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Organization */}
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      size="small"
                      fullWidth
                      freeSolo
                      loading={loadingDropdowns}
                      options={dropdownOptions.organizations}
                      value={Array.isArray(selectedOrganization) 
                        ? selectedOrganization.map(org => typeof org === 'string' ? { value: org, label: org } : org)
                        : selectedOrganization ? [{ value: selectedOrganization, label: selectedOrganization }] : []
                      }
                      onChange={(event, newValue) => {
                        const values = newValue.map(item => typeof item === 'string' ? item : item.value);
                        setSelectedOrganization(values);
                      }}
                      getOptionLabel={(option) => typeof option === 'string' ? option : (option.label || option.value || option)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Organization"
                          sx={{ 
                            '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                            '& .MuiInputBase-input': { fontSize: '0.85rem' }
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Designation */}
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      size="small"
                      fullWidth
                      freeSolo
                      loading={loadingDropdowns}
                      options={dropdownOptions.designations}
                      value={Array.isArray(selectedDesignation) 
                        ? selectedDesignation.map(des => typeof des === 'string' ? { value: des, label: des } : des)
                        : selectedDesignation ? [{ value: selectedDesignation, label: selectedDesignation }] : []
                      }
                      onChange={(event, newValue) => {
                        const values = newValue.map(item => typeof item === 'string' ? item : item.value);
                        setSelectedDesignation(values);
                      }}
                      getOptionLabel={(option) => typeof option === 'string' ? option : (option.label || option.value || option)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Designation"
                          sx={{ 
                            '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                            '& .MuiInputBase-input': { fontSize: '0.85rem' }
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Location */}
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      size="small"
                      fullWidth
                      freeSolo
                      loading={loadingDropdowns}
                      options={dropdownOptions.locations}
                      value={Array.isArray(selectedLocation) 
                        ? selectedLocation.map(loc => typeof loc === 'string' ? { value: loc, label: loc } : loc)
                        : selectedLocation ? [{ value: selectedLocation, label: selectedLocation }] : []
                      }
                      onChange={(event, newValue) => {
                        const values = newValue.map(item => typeof item === 'string' ? item : item.value);
                        setSelectedLocation(values);
                      }}
                      getOptionLabel={(option) => typeof option === 'string' ? option : (option.label || option.value || option)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Location"
                          sx={{ 
                            '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                            '& .MuiInputBase-input': { fontSize: '0.85rem' }
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Availability */}
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      size="small"
                      fullWidth
                      loading={loadingDropdowns}
                      options={dropdownOptions.availabilities}
                      value={dropdownOptions.availabilities.filter(opt => 
                        Array.isArray(selectedAvailability) 
                          ? selectedAvailability.includes(opt.value)
                          : selectedAvailability === opt.value
                      )}
                      onChange={(event, newValue) => {
                        const values = newValue.map(opt => opt.value);
                        setSelectedAvailability(values);
                      }}
                      getOptionLabel={(option) => option.label || option}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Availability"
                          sx={{ 
                            '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                            '& .MuiInputBase-input': { fontSize: '0.85rem' }
                          }}
                        />
                    )}
                  />
                </Grid>

                {/* Domain */}
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    size="small"
                    fullWidth
                    loading={loadingDropdowns}
                    options={dropdownOptions.domains}
                    value={dropdownOptions.domains.filter(opt => 
                      Array.isArray(selectedDomain) 
                        ? selectedDomain.includes(opt.value)
                        : selectedDomain === opt.value
                    )}
                    onChange={(event, newValue) => {
                      const values = newValue.map(opt => opt.value);
                      setSelectedDomain(values);
                    }}
                    getOptionLabel={(option) => option.label || option.value || option}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Domain"
                        sx={{ 
                          '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                          '& .MuiInputBase-input': { fontSize: '0.85rem' }
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Category */}
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    size="small"
                    fullWidth
                    loading={loadingDropdowns}
                    options={dropdownOptions.categories}
                    value={dropdownOptions.categories.filter(opt => 
                      Array.isArray(selectedCategory) 
                        ? selectedCategory.includes(opt.value)
                        : selectedCategory === opt.value
                    )}
                    onChange={(event, newValue) => {
                      const values = newValue.map(opt => opt.value);
                      setSelectedCategory(values);
                    }}
                    getOptionLabel={(option) => option.label || option.value || option}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Category"
                        sx={{ 
                          '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                          '& .MuiInputBase-input': { fontSize: '0.85rem' }
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              </Box>

              {/* Filter Footer with Action Buttons */}
              <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button                  
                    size="medium"
                    fullWidth
                    startIcon={<RefreshIcon />}
                    onClick={fetchAlumniData}
                    sx={{ fontSize: '0.8rem' }}
                  >
                    Apply Filters
                  </Button>
                  <Button                 
                    size="medium"
                    fullWidth
                    onClick={clearAllFilters}
                    sx={{ fontSize: '0.8rem' }}
                  >
                    Clear All
                  </Button>
                </Box>
              </Box>
            </Paper>

            {/* Right Side - Data Grid */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {/* Top Action Bar */}
              <Paper sx={{ m: 2, mb: 1, p: 1.5 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editMode}
                          onChange={(e) => setEditMode(e.target.checked)}
                          color="primary"
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                          Edit Mode
                        </Typography>
                      }
                    />
                    {editMode && editedCells.size > 0 && (
                      <Chip 
                        label={`${Array.from(editedCells.values()).reduce((sum, set) => sum + set.size, 0)} edited cell(s)`} 
                        size="small" 
                        color="warning"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                      {rowData.length} record{rowData.length !== 1 ? 's' : ''} found
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {editMode && editedCells.size > 0 && (
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <UpdateIcon />}
                        onClick={handleUpdateAll}
                        disabled={saving}
                        sx={{ fontSize: '0.75rem' }}
                      >
                        {saving ? 'Updating...' : `Update All (${Array.from(editedCells.values()).reduce((sum, set) => sum + set.size, 0)})`}
                      </Button>
                    )}
                    <Button                  
                      size="small"
                      startIcon={<RefreshIcon />}
                      onClick={fetchAlumniData}
                      sx={{ fontSize: '0.75rem' }}
                    >
                      Refresh
                    </Button>
                    <Button                 
                      size="small"
                      startIcon={<FileDownloadIcon />}
                      onClick={exportCsv}
                      disabled={rowData.length === 0}
                      sx={{ fontSize: '0.75rem' }}
                    >
                      Export CSV
                    </Button>
                    <Button
                      size="small"
                    
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={() => setAddDialogOpen(true)}
                      sx={{ fontSize: '0.75rem' }}
                    >
                      Add Alumni
                    </Button>
                  </Box>
                </Box>
              </Paper>

              {/* MUI DataTable with sticky header */}
              <Box sx={{ flex: 1, overflow: 'hidden', px: 2, pb: 2, display: 'flex', flexDirection: 'column' }}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box 
                    sx={{ 
                      flex: 1, 
                      overflow: 'auto',
                      '& .MuiTableContainer-root': { 
                        maxHeight: '100%',
                        position: 'relative',
                        '& thead': {
                          position: 'sticky',
                          top: 0,
                          zIndex: 10,
                          backgroundColor: 'background.paper',
                          '& th': {
                            backgroundColor: 'background.paper !important',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            position: 'sticky',
                            top: 0
                          }
                        }
                      }
                    }}
                  >
                    <DataTable
                      table={tableData}
                      entriesPerPage={{ defaultValue: 15, entries: [10, 15, 20, 25, 50] }}
                      canSearch={false}
                      showTotalEntries={true}
                      pagination={{ variant: "gradient", color: "info" }}
                      isSorted={true}
                      noEndBorder={false}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
      
      {/* Add Alumni Dialog */}
      <Dialog 
        open={addDialogOpen} 
        onClose={() => {
          setAddDialogOpen(false);
          setAddFormData({
            fullName: '',
            rollnum: '',
            customGender: '',
            customBatch: '',
            customCollegeStudied: '',
            customCourseStudied: '',
            customBranch: '',
            customWhatsappNumber: '',
            customPresentStatus: '',
            customOrganization: '',
            customDesignation: '',
            customLocation: '',
            customAvailability: '',
            domain: '',
            customCategory: ''
          });
          setAddFormErrors({});
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Add New Alumni</Typography>
            <IconButton onClick={() => setAddDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name *"
                  value={addFormData.fullName}
                  onChange={(e) => setAddFormData({ ...addFormData, fullName: e.target.value })}
                  error={!!addFormErrors.fullName}
                  helperText={addFormErrors.fullName}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Roll Number *"
                  value={addFormData.rollnum}
                  onChange={(e) => setAddFormData({ ...addFormData, rollnum: e.target.value })}
                  error={!!addFormErrors.rollnum}
                  helperText={addFormErrors.rollnum}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Gender"
                  value={addFormData.customGender}
                  onChange={(e) => setAddFormData({ ...addFormData, customGender: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  value={addFormData.customDob}
                  onChange={(e) => setAddFormData({ ...addFormData, customDob: e.target.value })}
                  size="small"
                  placeholder="dd/MM/yyyy"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Batch"
                  value={addFormData.customBatch}
                  onChange={(e) => setAddFormData({ ...addFormData, customBatch: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="College Studied"
                  value={addFormData.customCollegeStudied}
                  onChange={(e) => setAddFormData({ ...addFormData, customCollegeStudied: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Course Studied"
                  value={addFormData.customCourseStudied}
                  onChange={(e) => setAddFormData({ ...addFormData, customCourseStudied: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Branch"
                  value={addFormData.customBranch}
                  onChange={(e) => setAddFormData({ ...addFormData, customBranch: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="WhatsApp Number *"
                  value={addFormData.customWhatsappNumber}
                  onChange={(e) => setAddFormData({ ...addFormData, customWhatsappNumber: e.target.value })}
                  error={!!addFormErrors.customWhatsappNumber}
                  helperText={addFormErrors.customWhatsappNumber}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Present Status"
                  value={addFormData.customPresentStatus}
                  onChange={(e) => setAddFormData({ ...addFormData, customPresentStatus: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Organization"
                  value={addFormData.customOrganization}
                  onChange={(e) => setAddFormData({ ...addFormData, customOrganization: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Designation"
                  value={addFormData.customDesignation}
                  onChange={(e) => setAddFormData({ ...addFormData, customDesignation: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Location"
                  value={addFormData.customLocation}
                  onChange={(e) => setAddFormData({ ...addFormData, customLocation: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Availability"
                  value={addFormData.customAvailability}
                  onChange={(e) => setAddFormData({ ...addFormData, customAvailability: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Domain"
                  value={addFormData.domain}
                  onChange={(e) => setAddFormData({ ...addFormData, domain: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Category"
                  value={addFormData.customCategory}
                  onChange={(e) => setAddFormData({ ...addFormData, customCategory: e.target.value })}
                  size="small"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            
            color="secondary"
            startIcon={<ClearIcon />}
            onClick={handleResetAddForm}
            disabled={addingAlumni}
          >
            Reset
          </Button>
          <Button
            
            color="primary"
            startIcon={addingAlumni ? <CircularProgress size={16} /> : <AddIcon />}
            onClick={handleAddAlumni}
            disabled={addingAlumni}
          >
            {addingAlumni ? 'Adding...' : 'Add Alumni'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MUI Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

AdminAllAlumniDetailsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AdminAllAlumniDetailsDialog;

